import { z } from 'zod';

import {
  CHART_CALCULATION,
  CHART_COLOR_SCALE,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_FREQUENCY,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_SHARE_CALCULATION_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { getTimezoneDayjs } from '@/utils/time';

import {
  MAX_SIGNAL_CUSTOM_RANGE_DAYS,
  SIGNAL_DATE_PRESET,
} from './constants';

export const signalDatePresetSchema = z.enum(SIGNAL_DATE_PRESET);
export type SignalDatePresetT = z.infer<typeof signalDatePresetSchema>;

const signalCustomDateSelectionSchema = z
  .object({
    type: z.literal('custom'),
    startDate: z.string().datetime(),
    endDate: z.string().datetime(),
  })
  .superRefine((value, context) => {
    const start = getTimezoneDayjs(value.startDate).startOf('day');
    const end = getTimezoneDayjs(value.endDate).startOf('day');
    const today = getTimezoneDayjs().startOf('day');

    if (start.isAfter(end)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date must be on or before end date',
      });
    }

    if (end.isAfter(today)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Future dates are not supported',
      });
    }

    if (end.diff(start, 'day') + 1 > MAX_SIGNAL_CUSTOM_RANGE_DAYS) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Date range cannot exceed ${MAX_SIGNAL_CUSTOM_RANGE_DAYS} days`,
      });
    }
  });

export const signalDateSelectionSchema = z.union([
  z.object({
    type: z.literal('preset'),
    preset: signalDatePresetSchema,
  }),
  signalCustomDateSelectionSchema,
]);

export const storedSignalDateSelectionSchema = z.preprocess(
  (value) =>
    typeof value === 'string' &&
    Object.values(SIGNAL_DATE_PRESET).includes(value as SIGNAL_DATE_PRESET)
      ? { type: 'preset', preset: value }
      : value,
  signalDateSelectionSchema,
);

// Custom Chart State localStorage 영속화용 Zod schema
// target은 props에서 동적으로 주입되므로 저장 대상에서 제외
export const chartStateStorageSchema = z.object({
  type: z.nativeEnum(CHART_TYPE),
  historical: z.object({
    frequency: z.nativeEnum(CHART_FREQUENCY).nullable(),
    value: z.boolean(),
  }),
  graph: z.nativeEnum(CHART_GRAPH),
  graph_direction: z.nativeEnum(CHART_GRAPH_DIRECTION).optional(),
  field: z.object({
    type: z.nativeEnum(CHART_FIELD),
    entire: z.boolean(),
    candidates: z.array(z.union([z.string(), z.number()])).optional(),
  }),
  selected_field: z.object({
    method: z.nativeEnum(CHART_FIELD_ITEM_SELECTION_METHOD),
    items: z.array(z.union([z.string(), z.number()])).optional(),
  }),
  series: z.array(
    z.object({
      cross_field: z
        .object({
          type: z.nativeEnum(CHART_FIELD),
          entire: z.boolean(),
          candidates: z.array(z.union([z.string(), z.number()])).optional(),
        })
        .optional(),
      measure: z.object({
        metric: z.nativeEnum(CHART_METRIC),
        calculation: z.nativeEnum(CHART_CALCULATION),
      }),
      options: z
        .object({
          view_type: z.nativeEnum(CHART_METRIC_VIEW_TYPE).optional(),
          color_scale: z.nativeEnum(CHART_COLOR_SCALE).optional(),
          share_calculation_type: z
            .nativeEnum(CHART_SHARE_CALCULATION_TYPE)
            .optional(),
          scale_min: z.number().optional(),
          scale_max: z.number().optional(),
          show_row_total: z.boolean().optional(),
        })
        .optional(),
    }),
  ),
});

export type ChartStateStorage = z.infer<typeof chartStateStorageSchema>;
